// BETA 15.70 — Suno Prompt Library
//
// Per Dylan: "what i need added is based on what the item is there should
// be at least 3 copy able suno descriptions on what the sound could and
// should be for all weapons. that way i can click copy in game and create
// it in suno."
//
// PSBOT RUN's spec doc: docs/handoffs/from-psbotrun/2026-05-04-sound-
// inspector-suno-prompts-spec.md
//
// Suno authoring rules baked into these prompts:
//   - "X second [type] SFX" framing — NOT music language
//   - "sharp attack, fast decay" for short hits
//   - Avoid "build / crescendo / drone" in short SFX (triggers 12s outputs)
//   - Repeat duration twice for tight clips
//   - Key = "any" for percussive, "E minor" for musical (game's identity)
//   - BPM matters more than key for length
//
// Game audio identity:
//   - Game key: E minor (dark/metal aesthetic)
//   - Music BPM: 100-110, SFX BPM: 140-180

export type SoundSlot =
  | 'weapon_fire'      // primary attack sound
  | 'weapon_reload'    // mag/cylinder reload (guns only)
  | 'weapon_dryfire'   // out of ammo click (guns only)
  | 'weapon_hit'       // projectile impact on enemy
  | 'weapon_swing'     // melee swing whoosh (arc weapons)
  | 'weapon_throw'     // throwable launch (axe/voogoo/basketball)
  | 'weapon_loop_hum'  // continuous loop (auras like chad_aura)
  | 'weapon_charge'    // build-up sound (boss-shrine, ultimate)
  ;

export interface SunoSettings {
  type: 'one-shot' | 'loop';
  bpm: number;
  /** 'E minor' / 'C major' / 'any' */
  key: string;
  /** human readable: '0.5s', '2s loop' */
  durationHint: string;
}

export interface SoundPrompt {
  /** 'Realistic' / 'Doom-vibe' / 'Goopzone-comedy' / etc. */
  variantLabel: string;
  /** the Suno prompt text — copy-able as-is */
  prompt: string;
  settings: SunoSettings;
}

export interface ItemSoundEntry {
  /** matches weapon/item id from weapons.ts / bunchaBullshiiii.ts */
  itemId: string;
  /** for display */
  itemName: string;
  slot: SoundSlot;
  /** ALWAYS at least 3 */
  variants: SoundPrompt[];
}

export const SOUND_PROMPTS: ItemSoundEntry[] = [
  // ─── REVOLVER (Six Shooter) — cowboy starter ──────────────────────
  { itemId: 'revolver', itemName: 'Six Shooter', slot: 'weapon_fire', variants: [
    { variantLabel: 'Realistic', prompt: '0.5 second sharp revolver gunshot, classic six-shooter crack, brief gunpowder hiss, sharp transient, fast decay, single shot SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second crunchy distorted revolver gunshot, heavy bass thud + crackle, sharp attack, fast decay, gritty western SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second cartoony revolver pop, exaggerated cap-gun crack + tiny ricochet, single shot comedy SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.5s' } },
  ]},
  { itemId: 'revolver', itemName: 'Six Shooter', slot: 'weapon_reload', variants: [
    { variantLabel: 'Realistic', prompt: '1 second revolver reload, cylinder click + spin + chamber latch, mechanical foley, single SFX',
      settings: { type: 'one-shot', bpm: 140, key: 'any', durationHint: '1s' } },
    { variantLabel: 'Quick spin', prompt: '0.7 second fast revolver cylinder spin + snap closed, sharp metallic click, brief mechanical SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.7s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '1 second comedic revolver reload, exaggerated cylinder spin + spring boing + click, cartoon foley',
      settings: { type: 'one-shot', bpm: 140, key: 'any', durationHint: '1s' } },
  ]},
  { itemId: 'revolver', itemName: 'Six Shooter', slot: 'weapon_dryfire', variants: [
    { variantLabel: 'Realistic', prompt: '0.3 second empty revolver click, hammer fall on empty chamber, sharp metallic snap, dry SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Hollow', prompt: '0.3 second dry hammer click + faint chamber rattle, sharp empty-gun snap, brief SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.3 second cartoon dud noise, sad spring boing + tiny click, comedic out-of-ammo SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── PEANUT_RIFLE (tactical starter) — high-damage piercing ──────
  { itemId: 'peanut_rifle', itemName: 'Peanut Rifle', slot: 'weapon_fire', variants: [
    { variantLabel: 'Realistic', prompt: '0.5 second bolt-action rifle shot, sharp crack + brief echo tail + ejection ping, single sniper SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second heavy distorted rifle shot, sub-bass impact + crunchy crack + brief echo, brutal sniper SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second silly peanut-shooter shot, exaggerated wooden crack + cartoonish whistle + tiny echo, comedy rifle SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.5s' } },
  ]},
  { itemId: 'peanut_rifle', itemName: 'Peanut Rifle', slot: 'weapon_reload', variants: [
    { variantLabel: 'Realistic', prompt: '1.2 second bolt-action rifle reload, bolt pull-back + magazine clack + bolt forward + lock click, mechanical foley',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '1.2s' } },
    { variantLabel: 'Quick chambering', prompt: '0.8 second fast bolt cycle, sharp metallic clack-clack + chamber lock, brief mechanical SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.8s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '1.2 second comedic peanut-shell reload, crunchy peanut crack + spring boing + click, cartoon foley',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '1.2s' } },
  ]},

  // ─── SPATULA_SLASH (sponge starter) — melee arc ──────────────────
  { itemId: 'spatula_slash', itemName: 'Spatula Slash', slot: 'weapon_swing', variants: [
    { variantLabel: 'Realistic', prompt: '0.4 second metal spatula swing, sharp whoosh + brief metallic ring, single melee swipe SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.4 second heavy metal weapon swing, crunchy whoosh + low metallic shing, brutal melee SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second comedic spatula whip-swing, exaggerated whoosh + sticky sizzle, cartoon kitchen SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.4s' } },
  ]},
  { itemId: 'spatula_slash', itemName: 'Spatula Slash', slot: 'weapon_hit', variants: [
    { variantLabel: 'Realistic', prompt: '0.3 second melee impact thud, dull body hit + brief metallic clang, sharp attack, fast decay SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.3 second brutal melee impact, crunchy thud + sub-bass punch, sharp attack SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'E minor', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.3 second wet sizzle splat, hot pan slap + bubble pop, cartoon kitchen impact SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── RPK_MACHINE_GUN (killa starter) — full-auto ──────────────────
  { itemId: 'rpk_machine_gun', itemName: 'RPK Machine Gun', slot: 'weapon_fire', variants: [
    { variantLabel: 'Realistic', prompt: '0.15 second single MG round, sharp metallic crack + ejection ping, very brief automatic gunfire SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.15s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.15 second heavy distorted MG shot, crunchy crack + sub-bass thump, brutal automatic gunfire SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'E minor', durationHint: '0.15s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.15 second cartoony machine-gun pop, exaggerated bullet crack + tiny shell tinkle, comedy auto SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.15s' } },
  ]},
  { itemId: 'rpk_machine_gun', itemName: 'RPK Machine Gun', slot: 'weapon_reload', variants: [
    { variantLabel: 'Realistic', prompt: '1.5 second MG belt reload, magazine click out + new mag slap in + bolt cycle clack, mechanical foley',
      settings: { type: 'one-shot', bpm: 120, key: 'any', durationHint: '1.5s' } },
    { variantLabel: 'Quick reload', prompt: '0.8 second fast MG mag swap, sharp click-clack-clack, brief mechanical SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.8s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '1.5 second comedic MG reload, exaggerated mag drop + spring boing + click, cartoon foley',
      settings: { type: 'one-shot', bpm: 120, key: 'any', durationHint: '1.5s' } },
  ]},

  // ─── FIREBALL_WAND (wizard starter) — magical projectile ─────────
  { itemId: 'fireball_wand', itemName: 'Fireball Wand', slot: 'weapon_fire', variants: [
    { variantLabel: 'Magical', prompt: '0.6 second fireball cast, deep WHOOSH + crackling fire + brief magical chime, single spell SFX',
      settings: { type: 'one-shot', bpm: 140, key: 'E minor', durationHint: '0.6s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.6 second dark fireball spell, ominous whoosh + low crackle + sub-bass swell, evil magic SFX',
      settings: { type: 'one-shot', bpm: 140, key: 'E minor', durationHint: '0.6s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.6 second comedic fireball cast, exaggerated woosh + sizzle + cartoon zap, silly spell SFX',
      settings: { type: 'one-shot', bpm: 140, key: 'any', durationHint: '0.6s' } },
  ]},
  { itemId: 'fireball_wand', itemName: 'Fireball Wand', slot: 'weapon_hit', variants: [
    { variantLabel: 'Magical', prompt: '0.5 second fireball impact explosion, sharp BOOM + crackling fire tail, single magical impact SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second brutal fireball impact, heavy crunchy explosion + sub-bass + brief sizzle, dark magic impact SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second cartoon fireball splat, comedic boom + sizzle + smoke poof, silly impact SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
  ]},

  // ─── VIKING_AXE (throwable melee) ─────────────────────────────────
  { itemId: 'viking_axe', itemName: 'Viking Axe', slot: 'weapon_throw', variants: [
    { variantLabel: 'Realistic', prompt: '0.4 second axe throw, sharp grunt + heavy whoosh of spinning blade, single throw SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.4 second brutal axe hurl, low grunt + heavy distorted whoosh + metallic ring, viking metal SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second comedic axe toss, silly grunt + exaggerated whip-spin whoosh, cartoon throw SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
  ]},
  { itemId: 'viking_axe', itemName: 'Viking Axe', slot: 'weapon_hit', variants: [
    { variantLabel: 'Realistic', prompt: '0.4 second axe impact, brutal CHOP thud + brief metallic ring + bone crunch, single melee impact SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.4 second devastating axe impact, crushing thud + sub-bass + heavy metallic clang, brutal hit SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second cartoon axe smack, exaggerated thwack + boing + tiny clang, silly impact SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
  ]},

  // ─── VOOGOO_RAGER_TOSS (mr_silly starter) — throwable goop blobs ─
  { itemId: 'voogoo_rager_toss', itemName: 'VooGoo Rager Toss', slot: 'weapon_throw', variants: [
    { variantLabel: 'Goopzone-default', prompt: '0.5 second wet goop toss, slimy splash launch + multiple wet plops, gross comedy SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Heavier', prompt: '0.5 second heavy goop hurl, thick splat launch + low gurgle + 5 wet impacts, gross SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second brutal goop barrage, dark wet splat + low rumble + multiple impacts, evil slime SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.5s' } },
  ]},

  // ─── TACTICAL_BASKETBALL (lebron starter) — bouncing throwable ───
  { itemId: 'tactical_basketball', itemName: 'Tactical Basketball', slot: 'weapon_throw', variants: [
    { variantLabel: 'Realistic', prompt: '0.4 second basketball pass, leather grip squeak + air whoosh + bounce, sports SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Heavier', prompt: '0.4 second heavy basketball throw, deep thud + grip squeak + powerful whoosh, sports SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second comedic basketball toss, exaggerated dribble + cartoon whoosh + crowd cheer snippet, silly sports SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.4s' } },
  ]},
  { itemId: 'tactical_basketball', itemName: 'Tactical Basketball', slot: 'weapon_hit', variants: [
    { variantLabel: 'Realistic', prompt: '0.3 second basketball bounce-impact, rubber thud + brief leather slap, single bounce SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Heavier', prompt: '0.3 second heavy basketball slam-impact, deep thud + sub-bass + brief squeak, sports impact SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.3 second cartoon basketball bonk, exaggerated boing + thump + dribble snippet, silly impact SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── CHAD_AURA (chad starter) — continuous aura damage zone ───────
  { itemId: 'chad_aura', itemName: 'The Chad Aura', slot: 'weapon_loop_hum', variants: [
    { variantLabel: 'Doom-vibe', prompt: '2 second dark energy aura hum loop, low ominous drone + subtle electric crackle + sub-bass pulse, seamless loop, evil aura SFX',
      settings: { type: 'loop', bpm: 100, key: 'E minor', durationHint: '2s loop' } },
    { variantLabel: 'Magical', prompt: '2 second magical aura hum loop, ethereal pad + soft shimmer + low warm tone, seamless loop, fantasy aura SFX',
      settings: { type: 'loop', bpm: 100, key: 'E minor', durationHint: '2s loop' } },
    { variantLabel: 'Goopzone-comedy', prompt: '2 second comedic aura hum loop, weird buzzy synth + low gurgle + occasional bubble pop, seamless loop, silly aura SFX',
      settings: { type: 'loop', bpm: 100, key: 'any', durationHint: '2s loop' } },
  ]},
  { itemId: 'chad_aura', itemName: 'The Chad Aura', slot: 'weapon_hit', variants: [
    { variantLabel: 'Doom-vibe', prompt: '0.3 second aura damage tick, sharp electric zap + brief sizzle + sub-bass thump, dark energy hit SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'E minor', durationHint: '0.3s' } },
    { variantLabel: 'Magical', prompt: '0.3 second aura magical zap, bright spark + chime + brief crackle, magical hit SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'E minor', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.3 second comedic aura zap, exaggerated bzzt + cartoon shock + tiny pop, silly damage SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── BLUNDERBUSS (pirate starter) — wide cone shotgun ─────────────
  { itemId: 'blunderbuss', itemName: 'The Blunderbuss', slot: 'weapon_fire', variants: [
    { variantLabel: 'Realistic', prompt: '0.7 second blunderbuss blast, deep cannon-like BOOM + heavy gunpowder hiss + brief tail, antique shotgun SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '0.7s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.7 second brutal blunderbuss blast, crushing BOOM + sub-bass impact + crunchy hiss, dark pirate gun SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'E minor', durationHint: '0.7s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.7 second comedic blunderbuss puff, exaggerated boom + smoke poof + tiny rattle of pellets, silly antique gun SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '0.7s' } },
  ]},
  { itemId: 'blunderbuss', itemName: 'The Blunderbuss', slot: 'weapon_reload', variants: [
    { variantLabel: 'Realistic', prompt: '2 second blunderbuss muzzleload reload, ramrod scrape + powder pour + flintlock click, antique foley',
      settings: { type: 'one-shot', bpm: 100, key: 'any', durationHint: '2s' } },
    { variantLabel: 'Quick reload', prompt: '1.2 second fast blunderbuss reload, ramrod thump + cock back + click, brief mechanical SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '1.2s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '2 second comedic pirate gun reload, ramrod crank + spring boing + funny click, cartoon antique foley',
      settings: { type: 'one-shot', bpm: 100, key: 'any', durationHint: '2s' } },
  ]},

  // ─── WPN_ULTRA_BEAM — fast piercing energy beam ───────────────────
  { itemId: 'wpn_ultra_beam', itemName: 'Ultra Beam', slot: 'weapon_fire', variants: [
    { variantLabel: 'Sci-fi', prompt: '0.4 second sci-fi energy beam shot, sharp electric ZAP + high-pitched whine, single laser SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.4 second dark energy beam blast, crunchy distorted ZAP + sub-bass + electric crackle, brutal sci-fi SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second comedic ray-gun pew, exaggerated cartoon zap + sparkle, silly sci-fi SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.4s' } },
  ]},

  // ─── WPN_THE_TERMINATOR — boomerang ───────────────────────────────
  { itemId: 'wpn_the_terminator', itemName: 'The Terminator', slot: 'weapon_throw', variants: [
    { variantLabel: 'Sci-fi', prompt: '0.5 second futuristic boomerang launch, sharp metallic whip + electric hum + spinning whoosh, sci-fi throw SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second dark boomerang hurl, low ominous whoosh + electric crackle + spinning metal, evil sci-fi SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second comedic boomerang toss, cartoon whip-spin + tiny robot beep, silly sci-fi SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
  ]},
  { itemId: 'wpn_the_terminator', itemName: 'The Terminator', slot: 'weapon_hit', variants: [
    { variantLabel: 'Sci-fi', prompt: '0.3 second boomerang impact, sharp metallic clang + brief electric crackle, single sci-fi hit SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.3 second brutal boomerang impact, heavy metallic crunch + sub-bass + electric snap, dark sci-fi hit SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'E minor', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.3 second cartoon boomerang clang, exaggerated bonk + boing + tiny robot beep, silly impact SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── WPN_DRACS_FANG — melee arc with lifesteal ────────────────────
  { itemId: 'wpn_dracs_fang', itemName: "Drac's Fang", slot: 'weapon_swing', variants: [
    { variantLabel: 'Vampiric', prompt: '0.4 second vampire fang slash, sharp whoosh + brief blood-suck slurp + dark whisper, single vampiric melee SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.4 second brutal vampire claw swing, heavy whoosh + sub-bass + dark hiss, evil melee SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second comedic vampire bite swing, cartoon whoosh + funny chomp + tiny "blah" hiss, silly vampire SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.4s' } },
  ]},

  // ─── WPN_FROZEN_DARTS — icy dart gun ──────────────────────────────
  { itemId: 'wpn_frozen_darts', itemName: 'Frozen Darts', slot: 'weapon_fire', variants: [
    { variantLabel: 'Icy', prompt: '0.2 second icy dart shot, sharp THWIP + crystalline shimmer, single ice dart SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.2s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.2 second dark ice dart shot, sharp whip + low frozen hum + brief crackle, evil ice SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'E minor', durationHint: '0.2s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.2 second comedic ice dart pew, cartoony whip + tiny ice tinkle, silly cold SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.2s' } },
  ]},
  { itemId: 'wpn_frozen_darts', itemName: 'Frozen Darts', slot: 'weapon_hit', variants: [
    { variantLabel: 'Icy', prompt: '0.3 second ice dart impact + freeze, sharp impact + ice crystallization + brief shimmer, freeze hit SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.3 second brutal ice impact, dark crunch + frozen crackle + sub-bass, evil freeze SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'E minor', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.3 second cartoon ice splat, exaggerated freeze sound + tinkle + tiny "brrr", silly cold impact SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── WPN_HURRAY — confetti cannon ─────────────────────────────────
  { itemId: 'wpn_hurray', itemName: 'Hurray!', slot: 'weapon_fire', variants: [
    { variantLabel: 'Party', prompt: '0.5 second party confetti cannon, sharp pop + paper rustle + brief party horn, celebration SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'C major', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe (ironic)', prompt: '0.5 second dark celebration burst, distorted party horn + crunchy pop + sub-bass, ironic evil party SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second comedic confetti pop, exaggerated party blow + crowd cheer snippet + paper rustle, silly celebration SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'C major', durationHint: '0.5s' } },
  ]},

  // ─── WPN_EX_GIRLFRIEND — fan-pattern piercing shots ───────────────
  { itemId: 'wpn_ex_girlfriend', itemName: 'The Ex Girlfriend', slot: 'weapon_fire', variants: [
    { variantLabel: 'Realistic', prompt: '0.4 second multi-shot fan blast, 5 rapid sharp cracks fanning out, single multi-projectile SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.4s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.4 second brutal multi-shot fan, 5 distorted cracks + sub-bass + crunchy hiss, dark spread SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.4s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.4 second comedic angry-text-burst, rapid cartoon pops + sad sigh, silly emotional damage SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.4s' } },
  ]},

  // ─── WPN_KAREN_DRONES — orbiting contact-damage drone ─────────────
  { itemId: 'wpn_karen_drones', itemName: 'Karen Drones', slot: 'weapon_loop_hum', variants: [
    { variantLabel: 'Drone hum', prompt: '2 second drone propeller hum loop, mechanical whirring + electric buzz + pitch waver, seamless loop, drone SFX',
      settings: { type: 'loop', bpm: 100, key: 'any', durationHint: '2s loop' } },
    { variantLabel: 'Doom-vibe', prompt: '2 second dark drone hum loop, ominous mechanical whir + low electric drone + sub-bass pulse, seamless loop, evil drone SFX',
      settings: { type: 'loop', bpm: 100, key: 'E minor', durationHint: '2s loop' } },
    { variantLabel: 'Goopzone-comedy (Karen vibe)', prompt: '2 second annoying drone whine loop, cartoony nasally hum + occasional muffled "speak to your manager", silly drone SFX',
      settings: { type: 'loop', bpm: 110, key: 'any', durationHint: '2s loop' } },
  ]},
  { itemId: 'wpn_karen_drones', itemName: 'Karen Drones', slot: 'weapon_hit', variants: [
    { variantLabel: 'Realistic', prompt: '0.3 second drone propeller smack, sharp slap + brief whirr + grunt, single contact-damage SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.3 second brutal drone strike, heavy mechanical thud + electric crackle + sub-bass, dark drone hit SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'E minor', durationHint: '0.3s' } },
    { variantLabel: 'Goopzone-comedy (Karen)', prompt: '0.3 second comedic Karen-rage smack, exaggerated slap + tiny "EXCUSE ME" snippet, silly damage SFX',
      settings: { type: 'one-shot', bpm: 180, key: 'any', durationHint: '0.3s' } },
  ]},

  // ─── WPN_POOP_SLING — slingshot poop projectiles ──────────────────
  { itemId: 'wpn_poop_sling', itemName: 'Poop Sling', slot: 'weapon_fire', variants: [
    { variantLabel: 'Goopzone-default', prompt: '0.5 second slingshot poop launch, taut rubber stretch + wet release + plopping flight whoosh, gross comedy SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Heavier', prompt: '0.5 second heavy slingshot launch, deep rubber twang + wet thwack + low whoosh, gross heavy SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second dark slingshot blast, distorted rubber twang + low gurgle + sub-bass whoosh, evil gross SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.5s' } },
  ]},
  { itemId: 'wpn_poop_sling', itemName: 'Poop Sling', slot: 'weapon_hit', variants: [
    { variantLabel: 'Goopzone-default', prompt: '0.5 second wet poop splat impact, big juicy splat + lingering squelch, gross comedy impact SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Heavier', prompt: '0.5 second heavy wet poop slam, deep splat + sub-bass + bubbling tail, gross heavy impact SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second brutal gore-poop impact, dark crunchy splat + sub-bass + low squelch, evil gross hit SFX',
      settings: { type: 'one-shot', bpm: 160, key: 'E minor', durationHint: '0.5s' } },
  ]},

  // ─── WPN_SHITTY_CANNON — slow heavy cannonball ────────────────────
  { itemId: 'wpn_shitty_cannon', itemName: 'Shitty Cannon', slot: 'weapon_fire', variants: [
    { variantLabel: 'Realistic', prompt: '0.8 second old cannon shot, deep BOOM + smoke whoosh + brief whistling cannonball, antique cannon SFX',
      settings: { type: 'one-shot', bpm: 120, key: 'any', durationHint: '0.8s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.8 second brutal cannon blast, crushing BOOM + sub-bass + heavy smoke hiss, dark heavy artillery SFX',
      settings: { type: 'one-shot', bpm: 120, key: 'E minor', durationHint: '0.8s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.8 second comedic shitty cannon, weak boom + cartoon fizzle + tiny burp, silly antique gun SFX',
      settings: { type: 'one-shot', bpm: 120, key: 'any', durationHint: '0.8s' } },
  ]},
  { itemId: 'wpn_shitty_cannon', itemName: 'Shitty Cannon', slot: 'weapon_hit', variants: [
    { variantLabel: 'Realistic', prompt: '0.6 second cannonball impact, heavy thud + crackling debris + brief smoke hiss, single cannon impact SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '0.6s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.6 second brutal cannon impact, crushing crash + sub-bass + heavy debris, dark artillery hit SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'E minor', durationHint: '0.6s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.6 second cartoon cannon splat, exaggerated bonk + sad fart + tiny smoke poof, silly impact SFX',
      settings: { type: 'one-shot', bpm: 130, key: 'any', durationHint: '0.6s' } },
  ]},

  // ─── WPN_SQUIRTER — pink water gun continuous stream ──────────────
  { itemId: 'wpn_squirter', itemName: 'Squirter', slot: 'weapon_loop_hum', variants: [
    { variantLabel: 'Realistic', prompt: '1 second water stream loop, continuous pressurized squirt + light hiss, seamless loop, water gun SFX',
      settings: { type: 'loop', bpm: 140, key: 'any', durationHint: '1s loop' } },
    { variantLabel: 'Heavier', prompt: '1 second heavy water blast loop, thick pressurized stream + bubbling, seamless loop, water gun SFX',
      settings: { type: 'loop', bpm: 140, key: 'any', durationHint: '1s loop' } },
    { variantLabel: 'Goopzone-comedy', prompt: '1 second comedic squirt loop, exaggerated wet stream + occasional bubble pop, cartoon water gun SFX',
      settings: { type: 'loop', bpm: 140, key: 'any', durationHint: '1s loop' } },
  ]},

  // ─── WPN_UGLY_KITTENS — possessed kitten projectiles ──────────────
  { itemId: 'wpn_ugly_kittens', itemName: 'Ugly Kittens', slot: 'weapon_fire', variants: [
    { variantLabel: 'Spooky cats', prompt: '0.5 second possessed kitten launch, demonic kitten meow + whoosh + tiny scream, evil kitty SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second dark hellcat hurl, distorted demonic meow + sub-bass + dark whoosh, evil cat SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second cartoon ugly cat toss, exaggerated angry meow + cartoon whoosh + tiny hiss, silly cat SFX',
      settings: { type: 'one-shot', bpm: 150, key: 'any', durationHint: '0.5s' } },
  ]},
  { itemId: 'wpn_ugly_kittens', itemName: 'Ugly Kittens', slot: 'weapon_hit', variants: [
    { variantLabel: 'Spooky cats', prompt: '0.5 second kitten freeze hit, sharp icy crystallize + tiny demonic hiss + magical chime, freeze SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Doom-vibe', prompt: '0.5 second brutal kitten impact, dark crunch + frozen crackle + tiny scream, evil hit SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'E minor', durationHint: '0.5s' } },
    { variantLabel: 'Goopzone-comedy', prompt: '0.5 second comedic cat smack, cartoon bonk + angry meow + tiny boing, silly hit SFX',
      settings: { type: 'one-shot', bpm: 170, key: 'any', durationHint: '0.5s' } },
  ]},
];

/** Lookup helper for the Sound Inspector UI. Returns [] if no prompts. */
export function getSoundPrompts(itemId: string, slot: SoundSlot): SoundPrompt[] {
  const entry = SOUND_PROMPTS.find(e => e.itemId === itemId && e.slot === slot);
  return entry?.variants ?? [];
}

/** All unique slots that exist for a given itemId. */
export function getSlotsForItem(itemId: string): SoundSlot[] {
  return SOUND_PROMPTS.filter(e => e.itemId === itemId).map(e => e.slot);
}

/** All itemIds that have at least one prompt entry. Used by the inspector to show 🎵 indicator. */
export function hasPromptsForItem(itemId: string): boolean {
  return SOUND_PROMPTS.some(e => e.itemId === itemId);
}

/** Format a prompt + its settings as a copy-able multi-line string. */
export function formatPromptForCopy(p: SoundPrompt): string {
  const s = p.settings;
  const typeLabel = s.type === 'loop' ? 'Loop' : 'One-Shot';
  return `${p.prompt}\n\n[Settings: ${typeLabel} · BPM ${s.bpm} · Key ${s.key} · ~${s.durationHint} target]`;
}
